import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import palettesData from './palettes.json'
import { setMapColors } from './mapTheme'

// Type definitions
interface Palette {
  name: string
  mode: 'dark' | 'light'
  colors: Record<string, string>
  map: {
    walk: string[]
    drive: string[]
    motor: string[]
    ringBuffer: string
    selection: string
    highlight: string
  }
}

interface ThemeContextType {
  theme: string
  setTheme: (id: string) => void
  palettes: Record<string, Palette>
  currentPalette: Palette
  mapColors: Palette['map']
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Theme Provider Component
interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const STORAGE_KEY = 'geonexora-theme'
  const DEFAULT_THEME = 'geonexora-dark'

  const typedPalettes = palettesData as Record<string, Palette>

  // Initialize theme from localStorage or use default
  const [theme, setThemeState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved && typedPalettes[saved] ? saved : DEFAULT_THEME
    } catch {
      return DEFAULT_THEME
    }
  })

  const currentPalette = typedPalettes[theme]

  // Apply theme to document
  useEffect(() => {
    if (!currentPalette) return

    // Set data-theme attribute on html element for Tailwind
    document.documentElement.setAttribute('data-theme', currentPalette.mode)

    // Apply CSS custom properties
    const colors = currentPalette.colors
    Object.entries(colors).forEach(([key, value]) => {
      // Convert kebab-case to valid CSS variable name
      const cssVarName = `--gn-${key}`
      document.documentElement.style.setProperty(cssVarName, value)
    })

    // Sync map colors so non-React utilities pick up the current palette
    setMapColors(currentPalette.map)

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [theme, currentPalette])

  const setTheme = (newTheme: string) => {
    if (typedPalettes[newTheme]) {
      setThemeState(newTheme)
    }
  }

  const value: ThemeContextType = {
    theme,
    setTheme,
    palettes: typedPalettes,
    currentPalette,
    mapColors: currentPalette.map,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

// Hook to use theme
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
